import { useCallback } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { Button } from '@archipro/rocco/components/button';
import { CircleX } from 'lucide-react';
import { PaperclipLight, TimesLight } from '@archipro-design/icons';
import { toast } from 'sonner';
import {
    CONSTANTS,
    MIME_TYPE_MAPPING,
    truncateFilename,
} from '~/utils/enquiry-form-helper';

interface FileAttachmentProps {
    maxAttachments?: number;
    /**
     * Per-file size limit in bytes. Defaults to the shared enquiry cap
     * (`CONSTANTS.MAX_FILE_SIZE`). Get Matched passes a tighter cap so the
     * total payload stays under Mandrill's email-attachment ceiling.
     */
    maxFileSize?: number;
    files: File[];
    onChange: (files: File[]) => void;
    disabled?: boolean;
    /** compact renders a slim chip trigger (gate-flow forms) instead of the large button. */
    variant?: 'default' | 'compact';
}

const FileList = ({
    files,
    onChange,
    disabled,
}: {
    files: File[];
    onChange: (files: File[]) => void;
    disabled: boolean;
}) => (
    <div className="mt-2 flex flex-wrap gap-2">
        {files.map((file, index) => (
            <div
                key={index}
                className="mt-2 flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1.5 text-sm"
            >
                <span>{truncateFilename(file.name)}</span>
                <button
                    type="button"
                    onClick={() =>
                        onChange(files.filter((_, i) => i !== index))
                    }
                    disabled={disabled}
                    className={`text-gray-500 hover:text-gray-700 ${
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                >
                    <TimesLight className="h-4 w-4" />
                </button>
            </div>
        ))}
    </div>
);

export const FileAttachment = ({
    maxAttachments = 5,
    maxFileSize = CONSTANTS.MAX_FILE_SIZE,
    files,
    onChange,
    disabled = false,
    variant = 'default',
}: FileAttachmentProps) => {
    const onFileDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const totalFiles = files.length + acceptedFiles.length;
            if (totalFiles > maxAttachments) {
                toast.error(
                    `Maximum file count reached. You can upload ${maxAttachments} files at once`,
                );
                return;
            }

            // Handle rejected files
            if (fileRejections.length > 0) {
                const sizeRejections = fileRejections.filter((rejection) =>
                    rejection.errors.some(
                        (error) => error.code === 'file-too-large',
                    ),
                );
                const typeRejections = fileRejections.filter((rejection) =>
                    rejection.errors.some(
                        (error) => error.code === 'file-invalid-type',
                    ),
                );

                if (sizeRejections.length > 0) {
                    const fileNames = sizeRejections
                        .map((rejection) => rejection.file.name)
                        .join(', ');
                    toast.error(
                        `The following files exceed the maximum size limit of ${maxFileSize / (1024 * 1024)}MB: ${fileNames}`,
                    );
                }

                if (typeRejections.length > 0) {
                    const fileNames = typeRejections
                        .map((rejection) => rejection.file.name)
                        .join(', ');
                    toast.error(
                        `The following files have unsupported formats: ${fileNames}.`,
                    );
                }

                return;
            }

            // --- Extension-based validation for Safari and robustness ---
            const allowedExtensions = CONSTANTS.ALLOWED_FILES_ENQUIRY_INBOX.map(
                (ext) => ext.toLowerCase(),
            );
            const invalidExtensionFiles = acceptedFiles.filter((file) => {
                if (!file.name || typeof file.name !== 'string') return true;
                const extMatch = file.name?.match(/\.([^.]+)$/);
                if (!extMatch || !extMatch[1]) return true; // No extension
                const ext = extMatch[1].toLowerCase();
                return !allowedExtensions.includes(ext);
            });
            if (invalidExtensionFiles.length > 0) {
                const fileNames = invalidExtensionFiles
                    .map((f) => f.name)
                    .join(', ');
                toast.error(
                    `The following files have unsupported extensions: ${fileNames}.`,
                );
                return;
            }
            // --- End extension-based validation ---

            // If we have valid files, add them
            if (acceptedFiles.length > 0) {
                const mergedFiles = [...files, ...acceptedFiles];
                onChange(mergedFiles);
            }
        },
        [
	files,
	maxAttachments,
	maxFileSize,
	onChange
],
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: onFileDrop,
        multiple: true,
        disabled: files.length >= maxAttachments,
        noClick: files.length >= maxAttachments,
        noDrag: files.length >= maxAttachments,
        noKeyboard: files.length >= maxAttachments,
        accept: CONSTANTS.ALLOWED_FILES_ENQUIRY_INBOX.reduce(
            (acc, ext) => {
                const mimeTypes = MIME_TYPE_MAPPING[ext.toLowerCase()] || [
                    'application/octet-stream',
                ];
                mimeTypes.forEach((mimeType) => {
                    Object.assign(acc, {
                        [mimeType]: [...(acc[mimeType] || []), `.${ext}`],
                    });
                });
                return acc;
            },
            {} as { [key: string]: string[] },
        ),
        maxSize: maxFileSize,
    });

    if (variant === 'compact') {
        return (
            <div className="flex flex-col items-start gap-4">
                <div
                    {...getRootProps()}
                    className={
                        disabled || files.length >= maxAttachments
                            ? 'pointer-events-none'
                            : ''
                    }
                >
                    <input {...getInputProps()} />
                    <button
                        type="button"
                        className="inline-flex h-[37px] items-center gap-1.5 rounded-[var(--radius-btn-m)] border border-black bg-white px-4 text-[12px] font-medium tracking-[-0.02em] text-[#181818] transition-colors hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={disabled || files.length >= maxAttachments}
                    >
                        <PaperclipLight className="h-[13px] w-[13px] [&_path]:fill-current" />
                        Attach a File
                    </button>
                </div>
                {files.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#ededed] py-1.5 pr-2 pl-[11px] text-[12px] tracking-[-0.02em] text-[#181818]"
                            >
                                <span>{truncateFilename(file.name)}</span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        onChange(
                                            files.filter((_, i) => i !== index),
                                        )
                                    }
                                    disabled={disabled}
                                    aria-label={`Remove ${file.name}`}
                                    className={`grid place-items-center text-[#181818] transition-opacity hover:opacity-60 ${
                                        disabled
                                            ? 'cursor-not-allowed'
                                            : 'cursor-pointer'
                                    }`}
                                >
                                    <CircleX
                                        className="h-[13px] w-[13px]"
                                        strokeWidth={1.5}
                                    />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div
                {...getRootProps()}
                className={
                    disabled || files.length >= maxAttachments
                        ? 'pointer-events-none'
                        : ''
                }
            >
                <input {...getInputProps({ 'aria-label': 'Attach files' })} />
                <Button
                    type="button"
                    size="larger"
                    className="flex items-center gap-2 border border-gray-200 bg-gray-100 hover:border-gray-300 hover:bg-gray-200"
                    disabled={disabled || files.length >= maxAttachments}
                >
                    Attach files
                    <PaperclipLight className="h-4 w-4" />
                </Button>
            </div>
            <FileList files={files} onChange={onChange} disabled={disabled} />
        </div>
    );
};
